import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  LucideAngularModule,
  X,
  AlertTriangle,
  Trash2,
  Info,
} from 'lucide-angular';
import { BehaviorSubject } from 'rxjs';

export type ConfirmDialogVariant = 'danger' | 'warning' | 'info';

@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './confirm-dialog.html',
  styleUrl: './confirm-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmDialogComponent {
  private readonly isOpen$$ = new BehaviorSubject<boolean>(false);
  protected readonly isOpen$ = this.isOpen$$.asObservable();

  readonly title = input<string>('Confirm Action');
  readonly message = input<string>('Are you sure you want to proceed?');
  readonly confirmText = input<string>('Confirm');
  readonly cancelText = input<string>('Cancel');
  readonly variant = input<ConfirmDialogVariant>('danger');

  readonly confirmed = output<void>();
  readonly cancelled = output<void>();

  protected readonly icons = {
    X,
    AlertTriangle,
    Trash2,
    Info,
  };

  set isOpen(value: boolean) {
    this.isOpen$$.next(value);
  }

  get isOpen(): boolean {
    return this.isOpen$$.value;
  }

  protected getIcon() {
    switch (this.variant()) {
      case 'danger':
        return this.icons.Trash2;
      case 'warning':
        return this.icons.AlertTriangle;
      case 'info':
      default:
        return this.icons.Info;
    }
  }

  protected getIconColorClass(): string {
    switch (this.variant()) {
      case 'danger':
        return 'bg-red-100 text-red-600';
      case 'warning':
        return 'bg-amber-100 text-amber-600';
      case 'info':
      default:
        return 'bg-blue-100 text-blue-600';
    }
  }

  protected getConfirmButtonClass(): string {
    switch (this.variant()) {
      case 'danger':
        return 'bg-red-500 hover:bg-red-600 text-white';
      case 'warning':
        return 'bg-amber-500 hover:bg-amber-600 text-white';
      case 'info':
      default:
        return 'bg-blue-500 hover:bg-blue-600 text-white';
    }
  }

  protected onConfirm(): void {
    this.confirmed.emit();
    this.isOpen$$.next(false);
  }

  protected onCancel(): void {
    this.cancelled.emit();
    this.isOpen$$.next(false);
  }

  protected onBackdropClick(): void {
    this.onCancel();
  }
}
